// modules/audio-utils.js


// Fonctions utilitaires pour le traitement audio

/**
 * Calcule le RMS (Root Mean Square) d'un buffer audio
 */
export function calculateRMS(audioBuffer) {
  let sum = 0;
  for (let i = 0; i < audioBuffer.length; i++) {
    sum += audioBuffer[i] * audioBuffer[i];
  }
  return Math.sqrt(sum / audioBuffer.length);
}

/**
 * Normalise un AudioBuffer au niveau RMS cible
 */
export async function normalizeAudioBuffer(audioContext, audioBuffer, targetRMS = 0.1) {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const normalizedBuffer = audioContext.createBuffer(
    numberOfChannels,
    audioBuffer.length,
    audioBuffer.sampleRate
  );

  for (let channel = 0; channel < numberOfChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    const currentRMS = calculateRMS(channelData);
    const gainFactor = targetRMS / currentRMS;

    const normalizedData = new Float32Array(channelData.length);
    for (let i = 0; i < channelData.length; i++) {
      normalizedData[i] = channelData[i] * gainFactor;
    }
    normalizedBuffer.copyToChannel(normalizedData, channel);
  }

  return normalizedBuffer;
}

// Encodage MP3 lamejs
export async function optimizedMp3Encode(audioData, sampleRate, channels, progressCallback) {
    if (!window.lamejs) {
        throw new Error('La bibliothèque lamejs n\'est pas disponible');
    }

    try {
        const mp3encoder = new lamejs.Mp3Encoder(channels, sampleRate, 128);
        const mp3Data = [];
        const samples = new Int16Array(audioData.length);
        
        for (let i = 0; i < audioData.length; i++) {
            const sample = Math.max(-1, Math.min(1, audioData[i]));
            samples[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        }

        const blockSize = 1152;
        const numBlocks = Math.ceil(samples.length / blockSize);

        for (let i = 0; i < numBlocks; i++) {
            if (progressCallback) {
                progressCallback((i / numBlocks) * 100);
            }

            const start = i * blockSize;
            const end = Math.min(start + blockSize, samples.length);
            const leftChunk = samples.slice(start, end);

            const paddedChunk = new Int16Array(blockSize);
            paddedChunk.set(leftChunk);
            const mp3buf = mp3encoder.encodeBuffer(paddedChunk);
            if (mp3buf.length > 0) mp3Data.push(mp3buf);

            if (i % 10 === 0) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }

        const mp3buf = mp3encoder.flush();
        if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
        }

        return new Blob(mp3Data, { type: 'audio/mp3' });
    } catch (error) {
        console.error('Erreur lors de l\'encodage MP3:', error);
        throw error;
    }
}


// Conversion d’un élément audio vers MP3
export async function convertToMp3(audioElement, progressCallback) {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    try {
        const response = await fetch(audioElement.src);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const channelData = audioBuffer.getChannelData(0);
        return await optimizedMp3Encode(channelData, audioBuffer.sampleRate, 1, progressCallback);
    } catch (error) {
        console.error('Erreur lors de la conversion:', error);
        throw error;
    } finally {
        await audioContext.close();
    }
}


// Conversion depuis un AudioBuffer
export async function convertToMp3FromAudioBuffer(audioBuffer, progressCallback) {
    const channelData = audioBuffer.getChannelData(0);
    return await optimizedMp3Encode(channelData, audioBuffer.sampleRate, 1, progressCallback);
}


// Découper un audio à un instant donné
export async function splitAudioAtTime(audioElement, splitTime, updateProgressBar) {
    try {
        updateProgressBar(20);
        let audioBlob;
        if (!audioElement.src.includes('.mp3')) {
            audioBlob = await convertToMp3(audioElement, p => updateProgressBar(20 + p * 0.2));
        } else {
            const response = await fetch(audioElement.src);
            audioBlob = await response.blob();
        }

        updateProgressBar(40);
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        updateProgressBar(60);

        const splitIndex = Math.floor(splitTime * audioBuffer.sampleRate);
        const firstBuffer = audioContext.createBuffer(1, splitIndex, audioBuffer.sampleRate);
        firstBuffer.copyToChannel(audioBuffer.getChannelData(0).slice(0, splitIndex), 0);
        const secondBuffer = audioContext.createBuffer(
            1,
            audioBuffer.length - splitIndex,
            audioBuffer.sampleRate
        );
        secondBuffer.copyToChannel(audioBuffer.getChannelData(0).slice(splitIndex), 0);

        updateProgressBar(80);
        const firstBlob = await optimizedMp3Encode(firstBuffer.getChannelData(0), firstBuffer.sampleRate, 1);
        const secondBlob = await optimizedMp3Encode(secondBuffer.getChannelData(0), secondBuffer.sampleRate, 1);
        updateProgressBar(100);
        return [firstBlob, secondBlob];
    } catch (error) {
        console.error('Erreur lors du découpage:', error);
        throw error;
    } finally {
        updateProgressBar(0);
    }
}


// Générer les noms des parties découpées
export function generateSplitNames(baseName) {
    const lowerBaseName = baseName.toLowerCase();
    let prefix = baseName;
    let currentPart = 1;
    const partMatch = lowerBaseName.match(/(?:partie|part)\s*(\d+)/i);
    if (partMatch) {
        currentPart = parseInt(partMatch[1]);
        prefix = baseName.replace(/(?:partie|part)\s*\d+/i, '').trim();
    }
    return [`${prefix} Partie ${currentPart}`, `${prefix} Partie ${currentPart + 1}`];
}
